import {
  DnmRequestFailure,
  SHIPMENT_ACTION_ERROR_CODES,
  STOP_ERROR_CODES,
  type ShipmentActionErrorCode,
  type StopErrorCode,
  type StopField,
} from './dnmFailure';

const SHIPMENT_ACTION_MESSAGES: Record<ShipmentActionErrorCode, string> = {
  SHIPMENT_TRIP_STARTED:
    'This trip has already started, so it can’t be cancelled here. Refresh to see its current status.',
  SHIPMENT_ALREADY_CLOSED:
    'This shipment is already closed, so it can’t be cancelled. Refresh to see its current status.',
  SHIPMENT_POD_NOT_VERIFIED: 'Verify the proof of delivery before completing this shipment.',
  SHIPMENT_REASON_REQUIRED: 'Enter a reason, then try again.',
  SHIPMENT_BIDDING_OPEN:
    'Bidding is still open on this shipment. You can repost it after bidding closes.',
  SHIPMENT_INVALID_TRANSITION:
    'This action isn’t available for the shipment’s current status. Refresh to see its current status.',
};

/** Stop refusals (dnm-work#165 C2): what happened and the next step. VALIDATION_FAILED uses the field copy. */
const STOP_MESSAGES: Record<Exclude<StopErrorCode, 'VALIDATION_FAILED'>, string> = {
  STOP_ALREADY_OPEN:
    'This trip already has an open stop. Refresh to see it, then edit it or mark it moving again.',
  STOP_NOT_ALLOWED:
    'A stop can only be added while the trip is assigned or on the road. Refresh to see its current step.',
  STOP_CLOSED: 'This stop is already closed. Refresh to see the latest.',
  STOP_NOT_FOUND: 'This stop no longer exists. Refresh the trip.',
};

const STOP_FIELD_MESSAGES: Record<StopField, string> = {
  reason: 'Pick why the truck stopped.',
  payer: 'Pick who is responsible.',
  note: 'Add a short note (up to 500 characters). Other always needs one.',
  place: 'Keep the place to 255 characters.',
  expectedResumeAt: 'Pick a time later than now for “Expected to move again”.',
  value: 'Enter a whole number of hours from 0 to 168.',
};

/**
 * Copy for a refused action, shown inside its dialog and in the toast. Allow-listed shipment
 * codes get the approved copy; everything else keeps the safe per-status message. The API's
 * free-text `error` is never read.
 */
export function actionFailureMessage(error: unknown): string {
  if (!(error instanceof DnmRequestFailure)) return 'Something went wrong. Try again.';
  const code = SHIPMENT_ACTION_ERROR_CODES.find((c) => c === error.code);
  if (code) return SHIPMENT_ACTION_MESSAGES[code];
  const stopCode = STOP_ERROR_CODES.find((c) => c === error.code);
  if (stopCode === 'VALIDATION_FAILED') {
    const field = error.field as StopField | undefined;
    return field && field in STOP_FIELD_MESSAGES ? STOP_FIELD_MESSAGES[field] : error.message;
  }
  if (stopCode) return STOP_MESSAGES[stopCode];
  return error.message;
}
